import { MaterialIcons } from "@expo/vector-icons";
import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import { Formik, FormikErrors, FormikTouched } from "formik";
import type { Moment } from "moment";
// eslint-disable-next-line import/no-named-default
import { default as MomentLib } from "moment";
import { extendMoment } from "moment-range";
import { Button, Image, useTheme, View } from "native-base";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Linking, SafeAreaView, ScrollView, StyleSheet, Text } from "react-native";
import { Calendar, DateData, LocaleConfig } from "react-native-calendars";
import BasicDay from "react-native-calendars/src/calendar/day/basic";
import type { DayProps } from "react-native-calendars/src/calendar/day/index";
import { Icon } from "react-native-elements";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import type { MarkedDatesType } from "../../components/GroceryListDatePicker";
import { Routes, Scale, VerticalScale } from "../../constants";
import { FontFamily } from "../../constants/fonts";
import { getOrganisation } from "../../helpers/userHelpers";
import { setMenstrualCycle } from "../../slices/onboardingSlice";
import { userSelector } from "../../slices/userSlice";

// Create a typed moment instance with the range extension
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const moment = extendMoment(MomentLib);

// TODO: this is copied from Onboarding_7_NutritionPlanScreen.tsx - abstract this to a shared file
LocaleConfig.locales.en = {
  monthNames: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  monthNamesShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  dayNames: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  today: "Today",
};

LocaleConfig.locales.nl = {
  monthNames: [
    "Januari",
    "Februari",
    "Maart",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Augustus",
    "September",
    "Oktober",
    "November",
    "December",
  ],
  monthNamesShort: ["Jan", "Feb", "Mrt", "Apr", "Mei", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"],
  dayNames: ["Zondag", "Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag"],
  dayNamesShort: ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"],
  today: "Vandaag",
};
LocaleConfig.defaultLocale = "en";

// Custom day component that adds testIDs for Cypress testing
const CustomDayComponent: React.FC<DayProps & { date?: DateData }> = (props) => {
  const { date, ...otherProps } = props;
  const testID = date ? `native.calendar.SELECT_DATE_SLOT-${date.dateString}` : undefined;

  // BasicDay expects date as a string, not DateData object
  const dateString = date?.dateString;

  return <BasicDay {...otherProps} date={dateString} testID={testID} />;
};

interface MenstrualCycleDatesScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

interface MenstrualCycleFormValues {
  selectedDates: MarkedDatesType;
}

const MenstrualCycleDatesScreen: React.FC<MenstrualCycleDatesScreenProps> = ({ navigation }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Updated constants - removed MAX_PERIOD_LENGTH_DAYS as it's no longer needed
  const MAX_FUTURE_DAYS_ALLOWED = 14;

  const user = useSelector(userSelector);
  const organisation = user ? getOrganisation(user) : undefined;
  const logoUri = organisation?.logo;

  // Removed endDate state since we only track start date now
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [markedDates, setMarkedDates] = useState<MarkedDatesType>({});

  // Calculate the maximum selectable date (today + MAX_FUTURE_DAYS_ALLOWED)
  const maxSelectableDate = moment().add(MAX_FUTURE_DAYS_ALLOWED, "days").format("YYYY-MM-DD");

  // Simplified onDayPress to handle only single date selection
  const onDayPress = (day: DateData): void => {
    const date = moment(day.dateString);
    setStartDate(date);

    // Update marked dates to only show the selected date
    setMarkedDates({
      [day.dateString]: { selected: true, color: colors.primary[50], textColor: "white" },
    });
  };

  // Updated validation schema to only validate start date selection
  const validationSchema = Yup.object().shape({
    selectedDates: Yup.object()
      .test(
        "has-selected-date",
        t("onboarding.onboarding_menstrual_cycle_dates_screen.validation_error"),
        (value) => Object.keys(value || {}).length > 0
      )
      .test(
        "valid-date-range",
        t("onboarding.onboarding_menstrual_cycle_dates_screen.future_dates_error"),
        (value: MarkedDatesType) => {
          const dateKeys = Object.keys(value || {});
          if (dateKeys.length === 0) return false;

          const today = moment().startOf("day");
          const selectedDateStr = dateKeys[0]; // We only have one date now
          const selectedDate = moment(selectedDateStr);

          // Start date must be today or in the past
          return !selectedDate.isAfter(today);
        }
      ),
  });

  const handleFormSubmit = (values: MenstrualCycleFormValues): void => {
    // Get the selected date from the marked dates
    const selectedDateStr = Object.keys(values.selectedDates)[0];

    // Update the menstrual cycle data in the onboarding state with only start date
    dispatch(
      setMenstrualCycle({
        lastMenstrualPeriodStartDate: selectedDateStr,
        menstrualCycleLength: undefined, // This will be set in the next screen
      })
    );

    // Navigate to the next screen
    navigation.navigate(Routes.OnboardingMenstrualCycleSpacingScreen);
  };

  const disableArrowLeft = false;

  const handleGoBack = (): void => {
    navigation.goBack();
  };

  const handleInfoPress = (): void => {
    // FIXME: Remove this magic link
    Linking.openURL("https://killerbodyfood.com/blogs/voedingstips/tagged/menstruatiecyclus").catch((err) => {
      throw err;
    });
  };

  const renderCalendarArrow = (direction: "left" | "right"): React.ReactNode => (
    <MaterialIcons
      name={direction === "left" ? "chevron-left" : "chevron-right"}
      size={24}
      color={direction === "left" && disableArrowLeft ? colors.gray["300"] : colors.primary[50]}
    />
  );

  const renderValidationError = (
    error: FormikErrors<MarkedDatesType> | undefined,
    touched: FormikTouched<MarkedDatesType> | undefined
  ): React.ReactNode => {
    if (error && touched) {
      return <Text style={{ color: colors.error[500], marginTop: 5 }}>{error as unknown as string}</Text>;
    }
    return null;
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.primary[10] }]}>
      <View style={styles.header}>
        <Icon name="arrow-back" size={24} onPress={handleGoBack} testID="back-button" />
      </View>
      <ScrollView contentContainerStyle={styles.contentWrapper}>
        <View style={styles.content} testID="menstrualCycleDatePicker-container">
          {logoUri ? (
            <Image
              source={{ uri: logoUri }}
              style={{ width: Scale(160), height: VerticalScale(60) }}
              resizeMode="contain"
            />
          ) : (
            <Icon name="robot" type="material-community" size={64} color={colors.primary[50]} />
          )}
          <View style={styles.titleContainer}>
            <Text style={styles.title}>{t("onboarding.onboarding_menstrual_cycle_dates_screen.title")}</Text>
            <Icon
              name="info-circle"
              type="font-awesome-5"
              size={24}
              color={colors.primary[50]}
              onPress={handleInfoPress}
            />
          </View>
          <Formik<MenstrualCycleFormValues>
            initialValues={{ selectedDates: markedDates }}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
            enableReinitialize={true}
          >
            {({ handleSubmit, errors, touched }) => {
              const isSubmitDisabled = Object.keys(markedDates).length === 0;

              const handleNextButtonPress = (): void => {
                handleSubmit();
              };

              return (
                <View style={styles.calendarContainer} testID="menstrualCycleDatePicker-calendar">
                  <Calendar
                    monthFormat="MMMM"
                    onDayPress={onDayPress}
                    style={{ marginHorizontal: Scale(22), marginVertical: Scale(10) }}
                    // Changed to single selection marking type
                    markingType="custom"
                    markedDates={markedDates}
                    dayComponent={CustomDayComponent}
                    theme={{
                      textDayFontFamily: FontFamily.medium,
                      textMonthFontFamily: FontFamily.bold,
                      textDayHeaderFontFamily: FontFamily.medium,
                      textDayFontSize: Scale(14),
                      textMonthFontSize: Scale(16),
                      textDayHeaderFontSize: Scale(16),
                      arrowColor: colors.primary[600],
                      selectedDayBackgroundColor: colors.primary[600],
                      selectedDayTextColor: "white",
                    }}
                    firstDay={1}
                    renderArrow={renderCalendarArrow}
                    maxDate={maxSelectableDate}
                  />
                  {renderValidationError(errors.selectedDates, touched.selectedDates)}
                  <Button
                    onPress={handleNextButtonPress}
                    style={[styles.nextButton, { backgroundColor: colors.primary[600] }]}
                    isDisabled={isSubmitDisabled}
                    testID="menstrualCycleDatePicker-nextButton"
                  >
                    {t("onboarding.onboarding_menstrual_cycle_dates_screen.next_button")}
                  </Button>
                </View>
              );
            }}
          </Formik>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  contentWrapper: {
    flexGrow: 1,
  },
  content: {
    alignItems: "center",
    padding: 16,
  },
  titleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginTop: 16,
    textAlign: "center",
  },
  description: {
    fontSize: 16,
    textAlign: "center",
    marginVertical: 8,
  },
  calendarContainer: {
    width: "100%",
    marginTop: 16,
  },
  nextButton: {
    marginTop: 20,
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    width: "100%",
  },
});

export default MenstrualCycleDatesScreen;
